<template>
  <div class="flex">
    <PlaceCall
      v-if="entity?.[actionKey] && !showPhoneInRow"
      :source="source"
      :icon-action="true"
      :entity="entity"
      :multiple-entries="true"
      :trigger-open-dropdown="shouldOpenDropdown"
    />

    <a-button
      v-else-if="showPhoneInRow"
      class="secondary_small min-w-24 flex items-center justify-center gap-1"
      @click="accessPhoneNumber"
      :loading="isEnriching"
    >
      <ThemeImage
        v-if="!isEnriching"
        class="size-3 p-[1px] mb-[2px]"
        :icon="imageNames.WHITE_ENRICH_SEARCH"
        alt="enrich_search_icon"
      />

      <span class="font-semibold text-sm">{{ buttonText }}</span>
    </a-button>

    <CustomDropdown
      v-else
      ref="dropdownRef"
      gap="4px"
      position="end"
      :relative-to-viewport="true"
    >
      <template #trigger>
        <div class="quick_action_item mt-1" @click="handleDropdownClick">
          <ThemeImage
            class="size-5"
            :icon="imageNames.CALL_GREY"
            alt="call_icon"
          />
        </div>
      </template>

      <template #default>
        <div class="flex flex-col gap-2 p-2">
          <span class="text-[13px] secondary_color">Phone Number</span>

          <div v-if="notFound" class="flex flex-row items-center gap-1">
            <ThemeImage :icon="imageNames.ERROR" alt="error_icon" />
            <span class="text-sm text_color">Not available</span>
          </div>

          <div class="flex flex-col gap-2">
            <span v-if="!notFound" class="text-2xl font-semibold text_color"
              >************</span
            >
            <a-button
              v-if="!notFound"
              :disabled="isLoading"
              class="primary_medium min-w-48"
              @click="accessPhoneNumber"
              :loading="isEnriching"
            >
              <ThemeImage
                v-if="!isEnriching"
                :icon="imageNames.ENRICH_SEARCH"
                alt="enrich_search_icon"
                class="size-4"
              />
              {{ buttonText }}
            </a-button>
          </div>
        </div>
      </template>
    </CustomDropdown>
  </div>
</template>

<script setup>
import { message } from "ant-design-vue";
import {
  ref,
  onBeforeUnmount,
  onMounted,
  nextTick,
  computed,
  onUnmounted,
  watch,
} from "vue";
import { enrichWithApollo } from "@/src/utils/enrichUtils.js";
import { showAlreadyExistsMessage, isProspectV2Enabled } from "@/src/utils/utils.js";
import { handleApiError } from "@/src/utils/errorUtils.js";

import eventBus from "@/src/utils/eventBus.js";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import PlaceCall from "@/src/components/telephony_components/modules/PlaceCall.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { quickActionsTypes } from "@/src/constants/componentConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { ENRICH_FIELDS } from "@/src/constants/prospectConstants.js";
import { enrichProspectEntity as enrichProspectEntityUtil } from "@/src/utils/prospectEnrichmentUtils.js";
import DashboardService from "@/src/api/DashboardService.js";

const props = defineProps({
  row: { type: Object, required: true },
  action: { type: Object, required: true },
  fromProspect: { type: Boolean, required: false, default: false },
  source: { type: String, required: true },
  showPhoneInRow: { type: Boolean, required: false, default: false },
});

const entity = ref(null);
const dropdownRef = ref(null);
const requestID = ref(null);
const pollingAttempts = ref(0);
const pollingInterval = ref(null);
const notFound = ref(false);
const isLoading = ref(false);
const isEnriching = ref(false);
const actionKey = ref(props.action?.key || "phone_number");
const shouldOpenDropdown = ref(false);
const buttonText = computed(() => {
  return isEnriching.value ? "Accessing..." : "Access Phone Number";
});

const enrichingEventName = computed(() => `call-enriching-${props.row.id}`);
const notFoundEventName = computed(() => `call-notfound-${props.row.id}`);
const enrichmentRequestId = ref(null);

const setEnrichingState = (state, reqId = null) => {
  isEnriching.value = state;
  if (reqId) {
    enrichmentRequestId.value = reqId;
  }
  if (!state) {
    enrichmentRequestId.value = null;
  }
  eventBus.emit(enrichingEventName.value, state);
};

const setNotFoundState = (state) => {
  notFound.value = state;
  eventBus.emit(notFoundEventName.value, state);
};

const handleDropdownClick = () => {
  if (!entity.value?.[actionKey.value] && !dropdownRef.value?.isOpen) {
    fetchPendingRequest();
  }
};

const accessPhoneNumber = async () => {
  if (props.fromProspect) {
    await enrichProspectEntity();
  } else {
    await enrichCRMEntity();
  }
};

const placeCall = async () => {
  if (!entity.value?.[actionKey.value]) return;

  await nextTick();
  dropdownRef.value?.closeDropdown();
  shouldOpenDropdown.value = !shouldOpenDropdown.value;
};

const startPolling = () => {
  pollingAttempts.value = 0;
  stopPolling();

  pollingInterval.value = setInterval(async () => {
    pollingAttempts.value++;
    await fetchRequestStatus();

    if (pollingAttempts.value >= 20) {
      stopPolling();
      setEnrichingState(false);
      setNotFoundState(true);
    }
  }, 15000);
};

const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
    pollingInterval.value = null;
  }
  pollingAttempts.value = 0;
};

const enrichProspectEntity = async () => {
  setEnrichingState(true);
  const entityType = props.source === entityTypes.COMPANY ? entityTypes.COMPANY : entityTypes.LEAD;
  const payload = { ids: [entity.value?.id], enrich_fields: [ENRICH_FIELDS.PHONE] };

  try {
    const response = await enrichProspectEntityUtil(payload, entityType);

    if (isProspectV2Enabled() && response?.request_id) {
      setEnrichingState(true, response.request_id);
      eventBus.emit(`track-enrichment-${entity.value?.id}`, { requestId: response.request_id, enrichField: ENRICH_FIELDS.PHONE });
      return;
    }

    setEnrichingState(false);
    const leadData = response?.lead;
    const phone = leadData?.phone_number || leadData?.phone_numbers?.[0]?.phone_number;
    if (phone) {
      entity.value.phone_number = phone;
      entity.value[actionKey.value] = phone;
      if (leadData.phone_numbers?.length) entity.value.phone_numbers = leadData.phone_numbers;
      eventBus.emit("table-row-updated", { phone_number: phone, phone_numbers: entity.value.phone_numbers, action: quickActionsTypes.CALL, id: props.row.id });
      if (!props.showPhoneInRow) placeCall();
    } else if (response?.success === false) {
      showAlreadyExistsMessage({ customMessage: response?.message || response?.error });
    } else {
      setNotFoundState(true);
      message.info("No phone number found");
    }
  } catch (error) {
    handleApiError(error, message, (errorMessage) => {
      showAlreadyExistsMessage({ customMessage: errorMessage });
    });
    setEnrichingState(false);
  }
};

const handleEnrichmentCompleted = ({ entityId, enrichedData, status, enrichType, requestId }) => {
  if (entityId !== props.row?.id) return;
  if (enrichType && enrichType !== ENRICH_FIELDS.PHONE) return;
  if (!isEnriching.value) return;
  if (enrichmentRequestId.value && requestId && enrichmentRequestId.value !== requestId) return;
  
  setEnrichingState(false);
  
  if (status === "completed" && enrichedData?.phone_number) {
    entity.value.phone_number = enrichedData.phone_number;
    entity.value[actionKey.value] = enrichedData.phone_number;
    
    if (enrichedData.phone_numbers?.length) {
      entity.value.phone_numbers = enrichedData.phone_numbers;
    }
    
    eventBus.emit("table-row-updated", {
      ...enrichedData,
      action: quickActionsTypes.CALL,
      id: props.row.id,
    });
    
    if (!props.showPhoneInRow) {
      placeCall();
    }
  } else {
    setNotFoundState(true);
    message.info("No phone number found");
  }
};

const enrichCRMEntity = async () => {
  setEnrichingState(true);

  let source = props.source;
  if (props.fromProspect) {
    source = props.row?.contact_id ? entityTypes.CONTACT : entityTypes.LEAD;
  }
  try {
    const entityId = entity.value?.lead_id || entity.value?.contact_id || entity.value?.id;

    const response = await enrichWithApollo(
      entityId,
      source,
      false,
      true,
      true
    );
    if (response?.phone_number || response?.data?.phone_number) {
      entity.value = response;
      if (
        entity.value &&
        (response.phone_number || response.data?.phone_number)
      ) {
        entity.value[actionKey.value] =
          response.phone_number || response.data?.phone_number;
      }
      eventBus.emit("table-row-updated", {
        ...response.data,
        action: quickActionsTypes.CALL,
        id: props.row.id,
      });
      if (!props.showPhoneInRow) {
        placeCall();
      }
    } else if (response?.request_id) {
      requestID.value = response.request_id;
      startPolling();
    } else {
      setNotFoundState(true);
      message.info("No phone number found");
    }
  } catch (error) {
    handleApiError(error, message, (errorMessage) => {
      showAlreadyExistsMessage({ customMessage: errorMessage });
    });
  } finally {
    setEnrichingState(false);
  }
};

const fetchPendingRequest = async () => {
  if (entity.value?.phone_number) return;

  isLoading.value = true;
  let source = props.source;
  if (props.fromProspect) {
    source = props.row?.contact_id ? entityTypes.CONTACT : entityTypes.LEAD;
  }

  const payload = {
    entity_id: entity.value?.id,
    entity_type: source,
  };

  try {
    const response = await DashboardService.checkPendingRequest(payload);
    if (response?.success && response?.is_pending) {
      requestID.value = response?.request_id;
      setEnrichingState(true);
      startPolling();
    } else {
      setEnrichingState(false);
    }
  } catch (error) {
    setEnrichingState(false);
    console.error("Error fetching pending request:", error);
  } finally {
    isLoading.value = false;
  }
};

const fetchRequestStatus = async () => {
  if (!requestID.value) return;

  try {
    const response = await DashboardService.getWaterfallEnrichmentStatus(
      requestID.value
    );
    if (response?.success) {
      const data = response?.data || response;
      const status = response?.status || data?.status;
      if (status === "PENDING" || status === "pending") {
        setEnrichingState(true);
      }
      if (status === "COMPLETE" || status === "completed") {
        setEnrichingState(false);
        const phoneNumbers =
          data?.phone_numbers || data?.request_data?.phone_numbers;
        if (phoneNumbers && phoneNumbers.length > 0) {
          entity.value.phone_number = phoneNumbers[0].sanitized_number;
          entity.value[actionKey.value] = phoneNumbers[0].sanitized_number;
          eventBus.emit("table-row-updated", {
            phone_number: phoneNumbers[0].sanitized_number,
            action: quickActionsTypes.CALL,
          });
          if (dropdownRef.value?.isOpen) {
            placeCall();
          } else {
            message.success(
              `Found phone number for ${entity.value.first_name || ""} ${entity.value.last_name || ""}`
            );
          }
        } else {
          setEnrichingState(false);
          setNotFoundState(true);
        }
        stopPolling();
      }
    }
  } catch (error) {
    console.error("Error fetching request status:", error);
    setEnrichingState(false);
    setNotFoundState(true);
    stopPolling();
  }
};

watch(
  () => props.row,
  (newRow) => {
    if (newRow && newRow.phone_number && entity.value) {
      entity.value[actionKey.value] = newRow.phone_number;
    }
  },
  { deep: true }
);

watch(
  () => props.row,
  (newRow) => {
    if (newRow) {
      entity.value = newRow;
      if (newRow.phone_number && entity.value) {
        entity.value[actionKey.value] = newRow.phone_number;
      }
    }
  },
  { deep: true }
);

watch(
  () => entity.value?.[actionKey.value],
  (newValue) => {
    if (newValue) {
      notFound.value = false;
      setNotFoundState(false);
    }
  }
);

onBeforeUnmount(() => {
  stopPolling();
});

onMounted(async () => {
  entity.value = props.row;

  eventBus.on(enrichingEventName.value, (state) => {
    isEnriching.value = state;
  });

  eventBus.on(notFoundEventName.value, (state) => {
    notFound.value = state;
  });

  eventBus.on("prospect-enrichment-completed", handleEnrichmentCompleted);

  if (props.row && props.row.phone_number && entity.value) {
    entity.value[actionKey.value] = props.row.phone_number;
  }
});

onUnmounted(() => {
  eventBus.off(enrichingEventName.value);
  eventBus.off(notFoundEventName.value);
  eventBus.off("prospect-enrichment-completed", handleEnrichmentCompleted);
});
</script>
